import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../cn';

export interface ErrorStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  /** Friendly, human-readable message. NEVER pass a raw backend error string here. */
  message?: React.ReactNode;
  /** Optional retry handler; renders a retry affordance when provided. */
  onRetry?: () => void;
  retryLabel?: string;
  action?: React.ReactNode;
}

/**
 * EDS governed Error state — Design Bible §10; WP2 rule: never display raw
 * backend errors. Callers pass a friendly message; technical detail stays in
 * logs, not the UI.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'We could not complete this request. Please try again shortly.',
  onRetry,
  retryLabel = 'Try again',
  action,
  className,
  ...props
}) => (
  <div
    role="alert"
    className={cn(
      'flex flex-col items-center justify-center gap-3 rounded-eds-md border border-eds-danger/30 bg-eds-danger/8 px-6 py-12 text-center',
      className,
    )}
    {...props}
  >
    <span className="flex h-11 w-11 items-center justify-center rounded-eds-pill bg-eds-danger/12 text-eds-danger" aria-hidden="true">
      <AlertTriangle className="h-5 w-5" />
    </span>
    <div>
      <p className="font-eds-display text-base font-semibold text-eds-fg">{title}</p>
      {message && <p className="mt-1 max-w-sm font-eds-body text-sm text-eds-muted-fg">{message}</p>}
    </div>
    {(onRetry || action) && (
      <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-9 items-center rounded-eds-md border border-eds-border bg-eds-surface px-3.5 font-eds-body text-sm font-semibold text-eds-fg hover:bg-eds-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
          >
            {retryLabel}
          </button>
        )}
        {action}
      </div>
    )}
  </div>
);

export default ErrorState;
